<template lang="pug">
.author-home.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/editor/${firmCode}_${courseId}` : `#/editor/${courseId}`"
        )
          | Edit #[b {{ baseYamlFilename }}]

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/history/${firmCode}_${courseId}` : `#/history/${courseId}`"
        )
          | View #[b {{ baseYamlFilename }}] history
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible
    .max-w-screen-2xl.mx-auto
      .prose.prose.dark_prose-invert.max-w-none
        h1.font-normal 
          span Create audio for <b>{{ baseYamlFilename }} </b>  

        p.opacity-80 
          span(v-if="title")
            a(:href="`#/editor/${baseYamlFilename.slice(0, -4)}`") {{ title }} 
            |  
            | &ndash;
          span(v-if="lastUpdated") 
            | Last updated 
            |  
            a(:href="`#/history/${baseYamlFilename.slice(0, -4)}`") {{ formatTimeAgo(lastUpdated) }}

        template(v-if="loadCourseError")
          p.text-xl.italic 🚨 {{ loadCourseError }}

        form(v-else-if="hasSavedYaml").p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert

          fieldset(
            :disabled="statuses.createAIAudio !== 'idle'"
          )
            h2.p-0.mt-0
              span Current course languages: {{ courseLanguages.length }}
              span(v-if="deprecatedCount > 0") &nbsp;({{ deprecatedCount }} deprecated)

            .prose.dark_prose-invert
              .grid.grid-cols-4.gap-2
                label.inline-flex.items-center.gap-1(
                  :class="[{ 'p-2 bg-red-600/40': isDeprecated(l) }, { 'p-2 bg-orange-600/40': isMissingAIAudio(l) }, { 'p-2 bg-purple-600/40': isJitEnabledForLang(l) }]"
                  v-for="l in courseLanguages"
                )
                  input(
                    name="lang"
                    type="radio"
                    :value="l"
                    :disabled="isMissingAIAudio(l)"
                    @change="resetAudio"
                    v-model="selectedLanguage"
                  )
                  span {{ languagesMap[l].english }} [{{ l }}]

                  abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xl.font-bold(title="Deprecated language" v-if="isDeprecated(l)") 
                    span [?]

                  abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xl.font-bold(title="Missing AI audio" v-else-if="isMissingAIAudio(l)") 
                    span [?]

                  abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xl.font-bold(title="Audio AI JiT enabled for this language" v-else-if="isJitEnabledForLang(l)") 
                    span JiT

        template(v-if="!loadCourseError && !legacyAudioBlocked && audioScript.length === 0")
          p.animate-loading Generating audio script...

        template(v-else-if="!loadCourseError")
          form.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert

            h2.p-0.mt-0 Voice

            //- voice settings
            //- .flex.flex-row.gap-2
            p.p-0.mt-0.flex-1.prose.prose.dark_prose-invert Current voice settings: 
              code.bg-gray-800.text-yellow-400 {{ currentLanguageVoiceSettings }}
            p.p-0.mt-0.flex-end
              a.cursor-pointer(@click="fetchAllVoiceSettings" title="Download a TSV files with voice settings for ALL courses and languages") Download ALL

            label.inline-flex.items-center.gap-1
              input(
                name="lang"
                type="checkbox"
                :value="true"
                :disabled="statuses.createAIAudio !== 'idle'"
                @change="resetVoice"
                v-model="chooseVoice"
              )
              span Choose voice (leave blank for default/saved voice)

            fieldset(
              :disabled="statuses.createAIAudio !== 'idle'"
              v-if="chooseVoice"
            )
              p(v-if="filteredVoiceLanguages.length > 0")
                select(required class="w-full p-2 text-black border-2 rounded-md bg-gray-900" v-model="selectedVoiceLanguage" @change="changeVoiceLanguage")
                  option.bg-gray-800(value="" disabled selected v-if="filteredVoiceLanguages.length > 1") Select language
                  option.bg-gray-800(v-for="item in filteredVoiceLanguages" :value="item.Locale") {{ item.LocaleName }}

              p(v-if="selectedVoiceLanguage")
                select(class="w-full p-2 text-black border-2 rounded-md" v-model="selectedVoice" @change="changeVoice")
                  option.bg-gray-800(value="" disabled selected v-if="filteredVoices.length > 1") Select voice
                  optgroup.bg-gray-800(label='Native language voices')
                    option(v-for="(item, index) in filteredVoices" :value="item.ShortName") {{ item.DisplayName }} ({{ item.Gender }})
                  optgroup.bg-gray-800(
                    label='Multilingual voices'
                    v-if="filteredMultilingualVoices.length > 0"
                  )
                    option(v-for="(item, index) in filteredMultilingualVoices" :value="item.ShortName") {{ item.DisplayName }} ({{ item.Gender }}) - {{ item.LocaleName }}


              .sample-text(
                v-if="selectedVoice"
              )
                textarea.w-full.p-2.rounded-md.simple-scrollbar.font-monospace(
                  rows="5"
                  :placeholder="courseSampleVoiceText"
                  v-model="customSampleVoiceText"
                )

              p.flex.gap-2.items-center(
                v-if="selectedVoice"
              )
                button.h-10.btn-hover_cursor-pointer.bg-yellow-500.btn-hover_bg-yellow-600.disabled_bg-gray-600.disabled_opacity-80.group.text-gray-900.text-center.font-bold.py-2.px-4.rounded.flex.items-center.gap-2(
                  type="button"
                  :disabled="statuses.createAIAudio !== 'idle'"
                  @click="previewVoice" title="Preview voice"
                )
                  icon.text-2xl(
                    :icon="'ph:play-circle-fill'"
                    v-if="statuses.createVoicePreview !== 'pending'"
                  )
                  icon.text-2xl.animate-spin(
                    :icon="'ph:spinner'"
                    v-if="statuses.createVoicePreview === 'pending'"
                  )
                  span Preview

                audio.flex-1.h-10(
                  :class="['rounded-md', { 'hidden': !showVoicePreview }]"
                  ref="voicePreviewPlayer"
                  src=""
                  controls
                )

          form.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
            h2.p-0.mt-0 Create {{ languagesMap[selectedLanguage].english }} audio
            p.p-0.text-purple-700.dark_text-purple-300.font-medium(v-if="legacyAudioBlocked")
              | Audio AI JiT is enabled for {{ selectedLanguage }}. Tier-2 S3 batch audio generation is disabled for this language — narration is synthesized at runtime from 
              code course.audio-ai-jit
              |  in the course YAML.
            fieldset(
              :disabled="legacyAudioBlocked || (chooseVoice && selectedVoice == '')"
            )
              p
                button.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(type="button" :disabled="legacyAudioBlocked || statuses.createAIAudio !== 'idle'" @click="createAudio" v-if="statuses.createAIAudio !== 'success' && !legacyAudioBlocked") 
                  span.group-disabled_opacity-50 Create
                span(
                  :class="{ 'ms-4': statuses.createAIAudio !== 'success' }"
                )
                  span.text-red-700.dark_text-red-400.font-bold(v-if="statuses.createAIAudio === 'error'") {{ errors.createAIAudio }}
                  span.opacity-75(v-else-if="statuses.createAIAudio === 'pending'")
                    Icon.inline-block.me-2.text-2xl(:icon="'ph:spinner'").animate-spin
                    span.animate-loading Working...
                    span.ms-2 {{ timer }}
                  span.opacity-75(v-else-if="statuses.createAIAudio === 'success'") Done! Finished in {{ timer }}

              div(
                v-if="statuses.createAIAudio === 'success' && audioPath.length > 0"
              )
                p(
                  v-if="audioPath.length > 0"
                )
                  span Audio path saved:
                  code.w-full.simple-scrollbar.bg-gray-800.text-green-400 {{ audioPath }}

                p.mt-10.mb-0(
                  v-if="audioPath.length > 0"
                )
                  //- button.btn.bg-green-200.dark_bg-green-900.text-sm(
                  //-   @click="returnToEditor()"
                  //-   type="button"
                  //- ) {{ `Edit ${baseYamlFilename.slice(0,-4)}` }}

                  span
                    a.text-blue-500.hover_underline(:href="`#/editor/${baseYamlFilename.slice(0, -4)}`") Edit {{ baseYamlFilename }}

          .p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert(
            v-if="!legacyAudioBlocked && audioScript.length > 0"
          )
            h2.p-0.mt-0 Audio script
            p.text-yellow-200.font-monospace {{ audioJSON.length }} files
            textarea.font-monospace.rounded.text-yellow-200.p-4.simple-scrollbar.w-full(
              rows="15"
              readonly=true
              v-model="audioScriptOutput"
            )

    iframe.hidden(
      ref="courseAudioIFrame"
      referrerpolicy="origin-when-cross-origin"
      style="width: 800px; height: 600px; border: 0;"
      v-if="!loadCourseError"
    )

</template>

<script setup lang="ts">
import MainMenu from './MainMenu.vue';
import { formatTimeAgo } from '@vueuse/core';
import ToggleDark from './ToggleDark.vue';
import useDocTitle from '../lib/useDocTitle';
import { onMounted, ref, computed, reactive, watch, unref } from 'vue';
import { loadCourse } from '../store/courses';
import {
  createAIAudio,
  createVoicePreview,
  getVoiceList,
  getVoiceSettings,
  commitDraft,
  getLatestSaved
} from '../lib/callModContentAuthors';
import createAudioScript from '../../lib/createAudioScript.js';
import { Icon } from '@iconify/vue';
import jsYaml from 'js-yaml';
import { clearCourse, getCourse } from '../store/courses';
import getJWT from '../../lib/getJWT';
import yaml from 'yaml';
import extractBaseCourse from '../../lib/extractBaseCourse';
import languagesMap from '../../lib/languages.json';
import activeLanguages from '../../lib/activeLanguages.yml';
import {
  extractAudioAiJitConfig,
  hasAudioAiJitInParsedYaml,
  isAudioJitEnabledForLocale,
  type CourseAudioJitConfig
} from '../../lib/audioJitCourseConfig';

const props = defineProps<{ courseId: number; firmCode?: string }>();
const courseLanguages = ref([]);
const selectedLanguage = ref('');
const chooseVoice = ref<boolean>(false);
const title = ref<string>();
const hasSavedYaml = ref(false);
const loadCourseError = ref('');
const lastUpdated = ref<Date>();
const course = ref();
const courseAudioIFrame = ref<HTMLIFrameElement>();
const audioScript = ref('');
const audioAiJitConfig = ref<CourseAudioJitConfig | undefined>();
const audioJSON = computed(() =>
  audioScript.value ? JSON.parse(audioScript.value) : []
);
const audioScriptOutput = computed(() =>
  JSON.stringify(audioJSON.value, null, 2)
);
const audioPath = ref('');
const allVoiceData = ref([]);
const voiceSettings = ref([]);
const selectedVoiceLanguage = ref('');
const selectedVoice = ref('');
const voicePreviewPlayer = ref<HTMLAudioElement>();
const showVoicePreview = ref(false);
const customSampleVoiceText = ref('');
const timer = ref('00:00');
const courseAndFirm = {
  courseId: props.courseId,
  firmCode: props.firmCode
};

let interval: number;

const startTimer = () => {
  let seconds = 0;
  interval = setInterval(() => {
    seconds++;
    timer.value = new Date(seconds * 1000).toISOString().substr(14, 5);
  }, 1000);
  return interval;
};

const stopTimer = (interval: number) => {
  clearInterval(interval);
};

const audioPropCourse = computed(() => {
  return `audio:\n    ${selectedLanguage.value}: ${audioPath.value}`;
});

const audioPropPatch = computed(() => {
  return `$course.audio.${selectedLanguage.value}:\n    - value: ${audioPath.value}`;
});

const baseLanguage = computed(() => {
  if (selectedLanguage.value.indexOf('-') !== -1) {
    const splitLang = selectedLanguage.value.split(/-|_/);
    return splitLang[0].toLowerCase();
  } else {
    return selectedLanguage.value.toLowerCase();
  }
});

const currentLanguageVoiceSettings = computed(() => {
  /**
  Sample voice settings data array:
  [
    {
      "language":  "en-GB",
      "voice":  "en-GB-SoniaNeural",
      "speakingStyle":  "",
      "rate":  "1",
      "pitch":  "0"
    }
  ]
  */
  const defaultMessage =
    'No saved voice settings found (will use default voice)';
  if (
    !voiceSettings.value ||
    !voiceSettings.value.length ||
    !selectedLanguage.value
  )
    return defaultMessage;

  return (
    voiceSettings.value.find(
      item =>
        item.language?.toLowerCase() === selectedLanguage.value?.toLowerCase()
    )?.voice || defaultMessage
  );
});

// reduce the voice list to one for each language to create a lang list
const voiceLanguageList = computed(() => {
  return allVoiceData.value.filter(
    (item, index, self) =>
      index === self.findIndex(t => t.Locale === item.Locale)
  );
});

// filter to the same base language en-GB, en-US, en-IN etc. --> en (shows all English langs)
const filteredVoiceLanguages = computed(() => {
  return voiceLanguageList.value.filter((item, index, self) =>
    item.Locale.toLowerCase().startsWith(baseLanguage.value)
  );
});

const filteredVoices = computed(() => {
  return allVoiceData.value.filter(
    item =>
      item.Locale.toLowerCase() === selectedVoiceLanguage.value.toLowerCase()
  );
});

// also search for matches in the item's secondary language list (multilingual voices)
const filteredMultilingualVoices = computed(() => {
  return allVoiceData.value.filter(item =>
    item?.SecondaryLocaleList
      ? item?.SecondaryLocaleList.some(
          (lang: string) =>
            lang.toLowerCase() === selectedVoiceLanguage.value.toLowerCase()
        )
      : false
  );
});

const courseSampleVoiceText = computed(() => {
  // grab the first largish text block from the audio JSON
  let sampleText = '';
  let defaultSampleText = '';

  audioJSON.value.forEach(async audioItem => {
    if (sampleText.length > 0) return sampleText;

    Object.keys(audioItem).forEach(async filename => {
      let text = audioItem[filename];

      if (text.length >= 300) {
        sampleText = text.substring(0, 300) + '...';
        return;
      } else if (text.length >= 100) {
        defaultSampleText = text;
      }
    });
  });

  return (
    sampleText.split('\n').join('\n\n') ||
    defaultSampleText ||
    'Here is some sample text.'
  );
});

const copyToClipboard = async text => {
  return await navigator.clipboard.writeText(text);
};

const statuses = reactive<{
  [name: string]: 'idle' | 'pending' | 'success' | 'error';
}>({
  createAIAudio: 'idle',
  createVoicePreview: 'idle'
});

const errors = reactive<{
  [name: string]: string;
}>({
  createAIAudio: 'ERROR!',
  createVoicePreview: 'ERROR!'
});

const baseYamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

const url = computed(() => {
  let { latest } = course.value;
  const { courseId, firmCode, ubercourseVersion } = latest;
  const courseQ = `courseid=${courseId}`;
  const firmQ = firmCode ? `firm=${firmCode}` : '';
  const langQ = `lang=${selectedLanguage.value}`;
  const tagQ = 'tags=worddoc+includeall&audioDoc=1&ucAuthorAudio=1';
  const queryString = ['turbo=999999', courseQ, firmQ, langQ, tagQ].join('&');
  const localServer = window.location.origin.includes('local');
  const portalOrigin = new URL(window.location.href).origin;

  return localServer
    ? `./app.html?${queryString}`
    : `${portalOrigin}/latest/app.html?${queryString}`;
});

const previewVoice = async () => {
  statuses.createVoicePreview = 'pending';
  showVoicePreview.value = false;

  if (!chooseVoice.value) return;

  const data = {
    lang: selectedLanguage.value,
    text: customSampleVoiceText.value || courseSampleVoiceText.value,
    voiceData: {
      voice: selectedVoice.value
    }
  };

  await createVoicePreview(data)
    .then(audioData => {
      if (audioData) {
        statuses.createVoicePreview = 'success';
        const audioEl = voicePreviewPlayer.value as HTMLAudioElement;
        if (audioEl) {
          showVoicePreview.value = true;
          audioEl.src = audioData;
          audioEl.play();
        }
      }
    })
    .catch(e => {
      statuses.createVoicePreview = 'error';
      errors.createVoicePreview = 'Error creating voice preview';
      console.log('Voice preview error', e);
    });
};

const createAudio = async () => {
  if (isSelectedLanguageJitEnabled.value) return;

  let { latest } = course.value;
  const { courseId, firmCode } = latest;

  statuses.createAIAudio = 'pending';

  startTimer();

  console.log(
    'Creating audio:',
    courseId,
    firmCode,
    selectedLanguage.value,
    url.value
  );

  const data = {
    courseId,
    firmCode,
    lang: selectedLanguage.value,
    audioScript: audioScript.value,
    voiceData: chooseVoice.value
      ? {
          voice: selectedVoice.value
        }
      : '',
    uploadAudio: true
  };

  await createAIAudio(data)
    .then(response => {
      pollStatus(response?.summary)
        .then(async path => {
          audioPath.value = path;
          stopTimer(interval);

          statuses.createAIAudio = 'success';
          console.log('AI audio success', path);

          // update the displayed current voice
          fetchVoiceSettings();
        })
        .catch(e => {
          statuses.createAIAudio = 'error';
          console.log('Status error', e);
          stopTimer(interval);
        });
    })
    .catch(e => {
      statuses.createAIAudio = 'error';
      console.log('AI audio error', e);
      stopTimer(interval);
    });
};

const pollStatus = (summaryFilePath, interval = 5000) => {
  return new Promise((resolve, reject) => {
    const checkStatus = async () => {
      try {
        const summaryData = await fetch(summaryFilePath, {
          cache: 'no-cache'
        }).then(res => res.json());
        console.log('Checking status:', summaryData.status);
        if (summaryData.status === 'success') {
          // remove the filename to keep just the path
          const pathOnly = summaryFilePath.substring(
            0,
            summaryFilePath.lastIndexOf('/') + 1
          );
          resolve(pathOnly);
        } else if (summaryData.status === 'error') {
          reject(summaryData.status);
        } else {
          setTimeout(checkStatus, interval);
        }
      } catch (error) {
        reject(error);
      }
    };
    checkStatus();
  });
};

const returnToEditor = () => {
  // FIX: Monaco editor does not display latest saved yaml, even though it is loaded (bug?)
  // Update the URL to return to the editor page and force a reload
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + `#/editor/${baseYamlFilename.value.slice(0, -4)}`
  );

  window.location.reload();
};

const resetAudio = () => {
  chooseVoice.value = false;
  resetVoice();
};

const resetVoice = () => {
  selectedVoiceLanguage.value = '';
  changeVoiceLanguage();
};

const changeVoiceLanguage = () => {
  selectedVoice.value = '';
  changeVoice();
};

const changeVoice = () => {
  audioPath.value = '';
  statuses.createAIAudio = 'idle';
  statuses.createVoicePreview = 'idle';
  showVoicePreview.value = false;

  voicePreviewPlayer.value?.pause();
};

const load = async () => {
  try {
    course.value = await loadCourse(props);
    if (!course.value) throw new Error('Course not found.');
    let { latest, latestDraft, hasUnsavedChanges } = course.value;
    if (!latest?.courseId) throw new Error('Course not found.');

    lastUpdated.value = new Date(+latest.metadata.updatedDate);
    title.value = latest.title;
    hasSavedYaml.value = latest.yaml ? true : false;

    if (hasUnsavedChanges) {
      const draftAuthor = latestDraft?.author;
      throw new Error(`Course has unsaved changes from ${draftAuthor}.`);
    }

    const json = jsYaml.load(latest.yaml) as Content;
    let courseLangs = json.languages
      ? json.languages
      : json['default-language']
        ? [json['default-language']]
        : [];

    // we don't want to add en-GB to the audio lang list if it's not the base lang (e.g German-only courses)
    let isBaseLangEnglish =
      json['default-language'] && json['default-language'] !== 'en-GB'
        ? false
        : true;

    // if yaml doesn't contain languages, check the base course (no pages property means it's a full yaml and there is no base course)
    if (props.firmCode && !json?.pages && courseLangs.length === 0) {
      const baseCourseId = extractBaseCourse(latest.yaml) ?? props.courseId;
      const data = await getLatestSaved({ courseId: baseCourseId });
      const baseJson = jsYaml.load(data.yaml) as Content;
      isBaseLangEnglish =
        baseJson['default-language'] && baseJson['default-language'] !== 'en-GB'
          ? false
          : true;
      courseLangs = baseJson.languages
        ? baseJson.languages
        : baseJson['default-language']
          ? [baseJson['default-language']]
          : [];
    }

    // remove any en-GB from the languages array (it shouldn't be in there)
    courseLanguages.value = courseLangs.filter(l => l !== 'en-GB').sort();

    // if the base lang is English, include en-GB at the start of the lang list for creating audio
    if (isBaseLangEnglish) {
      courseLanguages.value.unshift('en-GB');
    }

    audioAiJitConfig.value = extractAudioAiJitConfig(
      json as Record<string, unknown>
    );
    // if the firm code is set and there are no pages in the parsed yaml (full yaml) and the audio AI JIT is not in the parsed yaml, check the base course
    if (
      props.firmCode &&
      !json?.pages &&
      !hasAudioAiJitInParsedYaml(json as Record<string, unknown>)
    ) {
      const baseCourseId = extractBaseCourse(latest.yaml) ?? props.courseId;
      const data = await getLatestSaved({ courseId: baseCourseId });
      const baseJson = jsYaml.load(data.yaml) as Record<string, unknown>;
      if (hasAudioAiJitInParsedYaml(baseJson)) {
        audioAiJitConfig.value = extractAudioAiJitConfig(baseJson);
      }
    }

    // select the first language
    selectedLanguage.value = courseLanguages.value[0];

    // check if both the url and iframe are available
    watch(
      [url, courseAudioIFrame, isSelectedLanguageJitEnabled],
      async () => {
        if (
          url.value &&
          courseAudioIFrame.value &&
          !isSelectedLanguageJitEnabled.value
        ) {
          await loadCourseInFrame();
        } else if (isSelectedLanguageJitEnabled.value) {
          audioScript.value = '';
        }
      },
      { immediate: true }
    );
  } catch (e) {
    loadCourseError.value = e.message;
  }
};

const loadCourseInFrame = async () => {
  if (isSelectedLanguageJitEnabled.value) return;

  audioScript.value = '';

  const iframe = courseAudioIFrame.value;
  if (!iframe || loadCourseError.value) return;

  await new Promise<void>((resolve, reject) => {
    const checkIframe = () => {
      const cw = iframe.contentWindow as Window;
      const pages = cw.document.documentElement.querySelector('.pages');
      if (pages) {
        resolve(pages);
      } else {
        setTimeout(checkIframe, 500);
      }
    };

    iframe.addEventListener('load', () => {
      const cw = iframe.contentWindow as Window;
      cw.onerror = (message, source, lineno, colno, error) => {
        loadCourseError.value = message;
        reject(
          new Error(
            `Error in iframe: ${message} at ${source}:${lineno}:${colno}`
          )
        );
      };

      if (cw.document.readyState === 'complete') {
        checkIframe();
      } else {
        cw.document.onreadystatechange = () => {
          if (cw.document.readyState === 'complete') {
            checkIframe();
          }
        };
      }
    });

    if (url.value) {
      iframe.src = url.value;
      console.log('loading iframe', url.value);
    }
  })
    .then(async pages => {
      audioScript.value = await createAudioScript(pages);
    })
    .catch(e => {
      console.log('Error loading course in iframe', e);
    });
};

const fetchVoiceData = async () => {
  await getVoiceList()
    .then(response => {
      allVoiceData.value = response.data.data.results;
    })
    .catch(e => {
      allVoiceData.value = [];
      console.log('Voice list error', e);
    });
};

const fetchVoiceSettings = async () => {
  await getVoiceSettings(props.firmCode, props.courseId)
    .then(response => {
      console.log('Voice settings', response.data.data.results);
      voiceSettings.value = response.data.data.results;
    })
    .catch(error => {
      if (error.response?.status === 404) {
        console.log('No voice settings found');
      } else {
        console.log('Voice settings error', error);
      }
    });
};

const fetchAllVoiceSettings = async () => {
  await getVoiceSettings('ALL', -1)
    .then(response => {
      const allVoiceSettings = response.data.data.results;
      if (allVoiceSettings.length > 0) {
        downloadVoiceSettingsTSV(allVoiceSettings);
      } else {
        console.log('No voice settings found');
      }
    })
    .catch(error => {
      if (error.response?.status === 404) {
        console.log('No voice settings found');
      } else {
        console.log('Voice settings error', error);
      }
    });
};

// Function to extract courseId and firmCode from settings filename
// 123-audio.json => { courseId: 1234, firmCode: '' }
// NATH_123-audio.json => { courseId: 123, firmCode: 'NATH' }
function parseFilename(filename) {
  const nameWithoutExt = filename.replace('-audio.json', '');
  const courseProps = nameWithoutExt.split('_');
  const courseId =
    courseProps.length === 1
      ? parseInt(courseProps[0])
      : parseInt(courseProps[1]);
  const firmCode = courseProps.length === 1 ? '' : courseProps[0];
  return { courseId, firmCode };
}

// Function to convert JSON data to TSV and trigger download
function downloadVoiceSettingsTSV(jsonData) {
  const tsvRows = ['courseId	firmCode	language	voice'];

  jsonData.forEach(file => {
    Object.entries(file).forEach(([filename, records]) => {
      const { courseId, firmCode } = parseFilename(filename);
      records.forEach(record => {
        tsvRows.push(`${courseId}	${firmCode}	${record.language}	${record.voice}`);
      });
    });
  });

  const tsvContent = tsvRows.join('\n');
  const blob = new Blob([tsvContent], { type: 'text/tab-separated-values' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'All-Voice-Settings.tsv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const languages = Object.keys(languagesMap)
  .filter(code => activeLanguages?.includes(code))
  .filter(code => !languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

const deprecatedLanguages = Object.keys(languagesMap)
  .filter(code => languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

// languages that are missing AI audio support
const missingAIAudioLanguages = Object.keys(languagesMap)
  .filter(code => !languagesMap[code].deprecated)
  .filter(code => languagesMap[code].audioVoice?.azure == null)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

const isDeprecated = lang => {
  return deprecatedLanguages.some(l => l.code === lang);
};

const isMissingAIAudio = lang => {
  return missingAIAudioLanguages.some(l => l.code === lang);
};

const deprecatedCount = computed(() => {
  return courseLanguages.value.filter(isDeprecated).length;
});

const isJitEnabledForLang = (lang: string) =>
  isAudioJitEnabledForLocale(audioAiJitConfig.value, lang);

const isSelectedLanguageJitEnabled = computed(() =>
  isJitEnabledForLang(selectedLanguage.value)
);

const legacyAudioBlocked = computed(() => isSelectedLanguageJitEnabled.value);

onMounted(async () => {
  await fetchVoiceData();
  await fetchVoiceSettings();
  await load();
});

const docTitle = computed(() => `Create audio ${baseYamlFilename.value}`);

useDocTitle(docTitle);
</script>

<style scoped></style>
